/**
 * Feature Notification Component
 * Shows a beautiful one-time modal when new features are available.
 * Displayed once per user per feature, dismissed permanently on close.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUndismissedFeatures, useDismissFeature } from '@/hooks/useFeatureAnnouncements';
import type { FeatureAnnouncement } from '@/hooks/useFeatureAnnouncements';
import { Button } from '@/components/ui/Button';
import {
  Sparkles,
  Upload,
  Map,
  TrendingUp,
  Activity,
  Mail,
  FileText,
  ArrowRight,
  X,
  ChevronRight,
  Wrench,
  Bug,
  Star,
} from 'lucide-react';

const ICON_MAP: Record<string, React.ElementType> = {
  Sparkles,
  Upload,
  Map,
  TrendingUp,
  Activity,
  Mail,
  FileText,
  Wrench,
  Bug,
  Star,
};

const COLOR_CONFIG: Record<string, { gradient: string; bgLight: string; ring: string }> = {
  blue: { gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50 dark:bg-blue-900/30', ring: 'ring-blue-500/20' },
  green: { gradient: 'from-green-500 to-emerald-600', bgLight: 'bg-green-50 dark:bg-green-900/30', ring: 'ring-green-500/20' },
  purple: { gradient: 'from-purple-500 to-purple-600', bgLight: 'bg-purple-50 dark:bg-purple-900/30', ring: 'ring-purple-500/20' },
  amber: { gradient: 'from-amber-500 to-orange-500', bgLight: 'bg-amber-50 dark:bg-amber-900/30', ring: 'ring-amber-500/20' },
  indigo: { gradient: 'from-indigo-500 to-indigo-600', bgLight: 'bg-indigo-50 dark:bg-indigo-900/30', ring: 'ring-indigo-500/20' },
  red: { gradient: 'from-red-500 to-red-600', bgLight: 'bg-red-50 dark:bg-red-900/30', ring: 'ring-red-500/20' },
};

const CATEGORY_LABELS: Record<string, string> = {
  feature: 'New Feature',
  improvement: 'Improvement',
  fix: 'Bug Fix',
};

export function FeatureNotification() {
  const navigate = useNavigate();
  const { data: undismissedFeatures, isLoading } = useUndismissedFeatures();
  const dismissFeature = useDismissFeature();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const features = undismissedFeatures || [];
  const hasFeatures = features.length > 0;
  const currentFeature = features[currentIndex];

  // Show the notification after a short delay so the app loads first
  useEffect(() => {
    if (hasFeatures && !isLoading) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [hasFeatures, isLoading]);

  const handleDismiss = async (feature: FeatureAnnouncement) => {
    try {
      await dismissFeature.mutateAsync(feature.id);
    } catch (err) {
      console.error('Failed to dismiss feature:', err);
    }
  };

  const handleClose = async () => {
    setIsClosing(true);
    // Dismiss the current feature
    if (currentFeature) {
      await handleDismiss(currentFeature);
    }
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleDismissAll = async () => {
    setIsClosing(true);
    // Dismiss all features
    for (const f of features) {
      await handleDismiss(f);
    }
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleNext = async () => {
    // Dismiss current and go to next
    if (currentFeature) {
      await handleDismiss(currentFeature);
    }
    if (currentIndex < features.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      handleClose();
    }
  };

  const handleGoToFeature = async () => {
    if (currentFeature?.route) {
      await handleDismiss(currentFeature);
      setIsVisible(false);
      navigate(currentFeature.route);
    }
  };

  if (!isVisible || !currentFeature) return null;

  const IconComponent = ICON_MAP[currentFeature.icon] || Sparkles;
  const colors = COLOR_CONFIG[currentFeature.color] || COLOR_CONFIG.blue;
  const categoryLabel = CATEGORY_LABELS[currentFeature.category] || 'Update';

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${colors.gradient} px-8 pt-8 pb-12 text-white relative overflow-hidden`}>
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-10 w-24 h-24 bg-white/10 rounded-full translate-y-1/2" />
          
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-white/80" />
              <span className="text-sm font-medium text-white/80 uppercase tracking-wide">
                {categoryLabel}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm ring-2 ring-white/30">
                <IconComponent className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{currentFeature.title}</h2>
                <p className="text-white/80 text-sm mt-0.5">{currentFeature.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {currentFeature.detailed_description && (
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              {currentFeature.detailed_description}
            </p>
          )}

          {currentFeature.route && (
            <div className={`${colors.bgLight} rounded-xl p-4 mb-6 flex items-center gap-3`}>
              <div className={`w-8 h-8 bg-gradient-to-br ${colors.gradient} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <ArrowRight className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {currentFeature.route_label || 'Try it out'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentFeature.route}</p>
              </div>
              <Button size="sm" onClick={handleGoToFeature}>
                Go
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {features.length > 1 && (
              <div className="flex items-center gap-1.5">
                {features.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentIndex ? 'bg-primary-500' : idx < currentIndex ? 'bg-primary-300' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                  {currentIndex + 1} of {features.length}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {features.length > 1 && (
              <Button variant="ghost" size="sm" onClick={handleDismissAll}>
                Dismiss All
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
            >
              {currentIndex < features.length - 1 ? (
                <>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              ) : (
                'Got it'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
